window.GUIDE_PERSPECTIVES = {
  V01: {
    A: {
      title: 'Between one location and the next',
      deck: 'Give the pause in your photography day a practical purpose.',
      intro: [
        'The next location is on the map, but the camera kit deserves a moment before you move on. Lay out the battery you need next, its matching dock and the lead that belongs with it.',
        'A compatible inverter can be part of that stop. Begin with the charger’s requirements and the vehicle socket limit, then find a dry, stable place for the equipment while parked.'
      ],
      focus: ['The next battery', 'A complete charging kit', 'A clear end to the stop']
    },
    B: {
      title: 'A workspace between appointments',
      deck: 'Build a mobile work session around the task in front of you.',
      intro: [
        'A parked vehicle can be the setting for a document review or a few messages between appointments. Think first about the work you intend to finish and the equipment it needs.',
        'The laptop, its compatible adapter and the inverter each need a place. Pure sine describes the output waveform; the useful workspace still depends on matched requirements, a permitted load and leads you can pack away.'
      ],
      focus: ['One defined work session', 'Room for the adapter', 'A workspace that packs away']
    },
    C: {
      title: 'The charging corner of a new room',
      deck: 'Arrive with a plan for the devices you unpack first.',
      intro: [
        'After a day of travel, it helps to know which lead belongs to the camera and which connection the next device needs. Make that small decision at home, with the labels in view.',
        'At your destination, compare the actual supply and socket with the plan before connecting. This step-down converter is one possible part of a checked setup; its shape and included adapters do not settle every compatibility question.'
      ],
      focus: ['Essential devices first', 'Labels before luggage', 'Check the actual socket']
    },
    D: {
      title: 'A place to plug in before the trip begins',
      deck: 'Give the navigation accessory and passenger devices a clear arrangement.',
      intro: [
        'Before departure, the console can collect several competing leads. A passenger brings a phone, a navigation accessory needs power, and another device appears just as everyone is ready to go.',
        'Use the parked moment to decide what will run together. The three accessory sockets add connection space, while the vehicle still sets a limit. Assign each device a checked output and a route that respects the people using the seats.'
      ],
      focus: ['What runs together', 'A clear console', 'Passenger-friendly cable routes']
    },
    E: {
      title: 'One charger, everyone’s own cable',
      deck: 'Make shared charging feel familiar to every passenger.',
      intro: [
        'A shared charger works better when each passenger recognizes their own lead. Bring the phones, tablets and cables together before the trip and give each combination an appropriate port.',
        'The six outputs offer different capabilities. Check the device’s supported charging method and the cable, then keep the pairing consistent. The goal is a simple arrangement passengers can use without asking the driver to work out a new connection on the road.'
      ],
      focus: ['A familiar cable pairing', 'The appropriate output', 'Easy passenger use']
    },
    F: {
      title: 'Make room for the desk you use',
      deck: 'Begin below the surface with the devices that belong in your working day.',
      intro: [
        'A desk changes gradually: another monitor, a different lamp, a laptop adapter that never quite found a place. Trace the leads before choosing where the next plug should go.',
        'Eight outlets offer room to organize, within the strip and circuit limits. Give each device a named connection, leave the switch accessible and keep the cable route visible enough to inspect when the furniture or equipment changes.'
      ],
      focus: ['Everyday equipment', 'A name for each lead', 'An accessible switch']
    }
  },
  V02: {
    A: {
      title: 'Does your camera kit need an inverter?',
      deck: 'Choose the charging route before choosing the power rating.',
      intro: [
        'Start by asking how your camera or battery dock is intended to charge. A supported USB route may suit the kit; an AC dock needs its own input compatibility check.',
        'If you need the AC route, compare the dock requirements with this inverter and your vehicle supply. The 300W continuous rating is only one limit in that chain. The vehicle socket may permit substantially less, so decide from the complete setup.'
      ],
      focus: ['USB or an AC dock?', 'What the dock requires', 'The lowest applicable limit']
    },
    B: {
      title: 'Is pure sine the right match for your work kit?',
      deck: 'Compare the requirements that make an inverter useful for your laptop.',
      intro: [
        'Pure sine output is a selection criterion when the device manufacturer requires it. It does not establish that every laptop needs this waveform or that any adapter will work.',
        'Keep the laptop’s compatible power supply in the comparison. Check its voltage, frequency and input requirements, then the vehicle socket’s allowance and any accessories you plan to add. Choose from those requirements before treating two available outlets as a complete mobile office.'
      ],
      focus: ['Waveform requirements', 'The correct laptop adapter', 'The complete connected load']
    },
    C: {
      title: 'Adapter, converter, or leave it at home?',
      deck: 'Three packing decisions begin with the device’s input label.',
      intro: [
        'A basic plug adapter changes the physical connection. A compatible voltage converter can serve a different need: changing voltage within its specified limits. First establish which job your device requires.',
        'This model provides a listed 110–120V AC output and requires a total load below 200W. It is unsuitable for the listed hair-appliance uses. Compare the device, destination and actual converter label; an incompatible item may be the easiest thing to remove from the packing list.'
      ],
      focus: ['Physical plug fit', 'Voltage compatibility', 'An honest packing decision']
    },
    D: {
      title: 'Do you need sockets, USB ports, or both?',
      deck: 'Choose extra connections for the equipment you actually bring.',
      intro: [
        'List the devices that require a vehicle accessory socket separately from those that can use a supported USB output. That tells you whether three sockets answer a real need in the car.',
        'Then compare input requirements and simultaneous use. The 180W maximum belongs to the splitter as a whole, subject to the vehicle limit. For USB-C, the listed maximum differs between 12V and 24V input; check the condition that applies to your supply.'
      ],
      focus: ['Accessory sockets or USB', 'Simultaneous-use requirements', 'The supply behind the rating']
    },
    E: {
      title: 'Choose the ports before the total',
      deck: 'Read a multi-port charger as a set of individual charging options.',
      intro: [
        'The 125W headline cannot tell you whether one particular phone, tablet or laptop will get the charging mode it needs. Start with each device and compare the individual outputs.',
        'This listing separates three USB-C PD ports up to 30W each, one USB-A QC 3.0 port up to 18W and two additional USB-A ports. Check the cable and simultaneous-use instructions too. A laptop deserves its own requirements check before it becomes part of the plan.'
      ],
      focus: ['Individual port capability', 'A compatible cable', 'A separate laptop decision']
    },
    F: {
      title: 'Choose a power strip around your desk',
      deck: 'Compare connection space, permitted load and surge protection separately.',
      intro: [
        'Begin with a list of the desk devices and their plug sizes. Eight outlets may offer useful spacing, but the actual adapters still need room to seat fully and the switch needs to remain accessible.',
        'The 2000-joule surge rating answers a different question from the strip’s permitted electrical load. Read the actual product label and manual for that limit. Choose a layout with a direct connection to a suitable grounded wall outlet and a route you can inspect.'
      ],
      focus: ['Plug clearance', 'Actual label limits', 'A serviceable cable route']
    }
  },
  V03: {
    A: {
      title: 'Pack the camera charging stop',
      deck: 'Prepare a complete kit, use a checked location, and account for every part afterward.',
      intro: [
        'Before departure, pair the battery dock with its correct lead and record the input requirements. Keep that pair easy to reach so you can check the whole arrangement during a planned stop.',
        'While parked, choose a dry surface and clear cable route, within the vehicle and inverter instructions. When the session ends, disconnect and collect the dock, battery and leads. A repeatable pack-down routine helps keep a small charging stop from scattering the camera kit.'
      ],
      focus: ['Pack a complete pair', 'Set up while parked', 'Count the parts afterward']
    },
    B: {
      title: 'A mobile work kit that packs away cleanly',
      deck: 'Plan the setup and pack-down before the first appointment.',
      intro: [
        'Put the laptop, compatible adapter and inverter on a surface before packing. Check the input requirements and the vehicle socket rating, then decide which accessories the day actually calls for.',
        'At the work stop, leave both power devices ventilated and arrange the leads clear of doors and moving furniture. Save room in the schedule to finish, disconnect and stow everything according to its instructions. The work surface should be ready to clear before the vehicle moves.'
      ],
      focus: ['Check the kit at home', 'Keep the route clear', 'Finish before moving on']
    },
    C: {
      title: 'Build a charging plan for every stop',
      deck: 'Connect the packing list to the itinerary, one device and destination at a time.',
      intro: [
        'Create a short device card with input voltage, frequency, power information and required connection. Add the verified supply and socket type for each destination so the plan covers more than the first room you check into.',
        'Compare those notes with the label on the actual converter before use. Keep compatible leads and the required adapters together, and leave excluded appliances out. Use the same list when packing to return home, with every item accounted for.'
      ],
      focus: ['A device label card', 'Every destination checked', 'A complete return kit']
    },
    D: {
      title: 'A charging map before departure',
      deck: 'Assign the connections before passengers settle in.',
      intro: [
        'Write down the devices expected to run together and the output each needs. Check their requirements against the splitter, individual output limits and the vehicle socket before you decide which leads to pack.',
        'Lay out the disconnected arrangement while the car is stationary. Follow each cable past the console, seat adjustments and passenger exits. Before departure, confirm the checked setup; if it needs changing later, wait until safely stopped so the driver can focus on the road.'
      ],
      focus: ['List what runs together', 'Inspect the disconnected route', 'Adjust only when stopped']
    },
    E: {
      title: 'Pair the passenger cables before you leave',
      deck: 'Turn six possible connections into a small, understandable travel kit.',
      intro: [
        'Gather the devices that will actually make the trip, then match each one with a supported output and suitable cable. Mark or pack the pairs so passengers can recognize their own lead.',
        'Check reach from the seats before connecting, allowing movement without loops across controls or exits. Keep the original pairing in mind if charging needs attention during a stop. At the end of the trip, collect the leads with their devices so the next journey begins with a complete kit.'
      ],
      focus: ['Match before packing', 'Check from the passenger seat', 'Collect the pairs afterward']
    },
    F: {
      title: 'Reset the desk when you get home',
      deck: 'Give the laptop and everyday equipment a clear place to return to.',
      intro: [
        'Unpacking can be a useful moment to review the home desk. Before reconnecting the laptop adapter, trace the existing monitor and lamp leads and remove unused cables from the planned arrangement.',
        'Check the strip label, combined load and plug clearance again if the equipment has changed. Give the desk a direct connection to a suitable grounded wall outlet and a visible cable route. Keep the returned laptop adapter with its device, ready for the next working day.'
      ],
      focus: ['Trace the home desk leads', 'Recheck changed equipment', 'Keep the switch accessible']
    }
  },
  V04: {
    A: {
      title: 'Where the 300W rating fits',
      deck: 'Follow the power path from the vehicle socket to the camera dock.',
      intro: [
        'There are several checks in a camera charging path: the vehicle supply, the inverter and the dock’s input requirements. Each part contributes a limit or compatibility condition to the complete arrangement.',
        'This inverter’s 300W continuous rating does not make 300W available from every vehicle socket. The listing warns against more than 150W through the cigarette-lighter connection, and the vehicle can allow less. Read the guide as a map of those separate checks before connecting a compatible dock.'
      ],
      focus: ['Vehicle supply', 'Inverter output', 'Dock input requirements']
    },
    B: {
      title: 'Waveform, voltage, and watts',
      deck: 'Understand the specifications by the different questions they answer.',
      intro: [
        'Pure sine wave describes the intended shape of the inverter’s AC output. Voltage and frequency describe other characteristics the device must accept. Available continuous power is another separate limit.',
        'Use those distinctions to read your laptop adapter’s requirements and the inverter specification together. Then include the vehicle socket’s allowance. A waveform description cannot remove a lower supply limit, and a spare outlet does not establish room for an additional device.'
      ],
      focus: ['Waveform describes shape', 'Input requirements establish fit', 'The lowest limit still applies']
    },
    C: {
      title: 'The two jobs of a travel connection',
      deck: 'Separate physical plug fit from the voltage a device accepts.',
      intro: [
        'A plug adapter changes the physical connection to the socket. A voltage converter changes voltage within its stated limits. They answer different questions, so the shape of a plug cannot complete the compatibility check.',
        'This model is listed with a 110–120V AC output and a total connected load below 200W. Check its actual input label against the destination, then the device’s voltage, frequency and restrictions. Trace both sides of the converter before choosing what to pack.'
      ],
      focus: ['Destination supply', 'Converter input and output', 'Device requirements']
    },
    D: {
      title: 'Why three sockets still share one limit',
      deck: 'Read the splitter as several branches from a single vehicle supply.',
      intro: [
        'A splitter gives equipment more connection points. It does not increase the power available from the vehicle socket. That is why simultaneous devices need to be considered together, including the USB charging in use.',
        'The 180W maximum is a product-level figure, subject to lower limits elsewhere. USB-C adds a separate condition: up to 36W at 12V input and up to 65W at 24V input. Follow the condition that matches the actual supply, then check the device and cable.'
      ],
      focus: ['One shared supply', 'Several connection points', 'Conditional USB-C output']
    },
    E: {
      title: 'Read the six ports as a map',
      deck: 'Connector, charging method and output rating each tell part of the story.',
      intro: [
        'Three USB-C PD ports, a USB-A QC 3.0 port and two additional USB-A ports are different options within one charger. The 125W headline does not describe the output available from a single connection.',
        'Read the port’s supported charging method and rating, then compare the cable and device requirements. A familiar connector does not establish the complete charging capability. Use the port map to choose a supported combination, and check the manual for simultaneous-use conditions.'
      ],
      focus: ['Connector type', 'Supported charging method', 'Individual output rating']
    },
    F: {
      title: 'Outlets, watts, and joules are different',
      deck: 'Three common power-strip details describe three different things.',
      intro: [
        'Outlet count describes connection space. A permitted load describes how much equipment the supply can serve within its ratings. The marketed 2000-joule figure describes a surge-protection energy rating, not 2000W of desk power.',
        'Keep those ideas separate when planning a monitor, laptop adapter and lamp. The actual strip label and manual establish its operating limits, while the wall circuit also matters. Surge protection does not provide stored energy to keep the computer running through an outage.'
      ],
      focus: ['Connection space', 'Permitted operating load', 'Surge-protection energy rating']
    }
  },
  V05: {
    A: {
      title: 'Keep the camera charging kit simple',
      deck: 'One priority battery, one checked charging route, one deliberate stop.',
      intro: [
        'Choose the battery you need next and pack its complete charging kit together. If the camera supports a suitable USB route, consider that first; if it requires the AC dock, check the dock and inverter requirements.',
        'Keep the stop easy to manage: park, use a dry and stable location, stay within the vehicle’s allowance and disconnect before packing. Add a second device only after checking what it changes in the combined load and layout.'
      ],
      focus: ['Prioritize one battery', 'Check the whole route', 'Finish with a complete kit']
    },
    B: {
      title: 'Bring the work essentials',
      deck: 'A considered mobile setup starts with a modest equipment list.',
      intro: [
        'Choose the work you plan to finish, then bring the laptop and compatible adapter it requires. Treat an extra display or another accessory as a new compatibility and load decision.',
        'Select pure sine output when it fits the equipment requirements, while checking voltage, frequency and the vehicle socket limit independently. Keep the adapter and inverter ventilated and accessible. Leave time to disconnect and put the workspace away before the next part of the journey.'
      ],
      focus: ['Define the work', 'Choose the required equipment', 'Leave room to pack away']
    },
    C: {
      title: 'Pack with the labels checked',
      deck: 'Give each essential device a supported connection and leave the guesses behind.',
      intro: [
        'Make a short list of the devices worth carrying. Read the input labels, confirm the destination supply and socket, and decide which items need a plug adapter or a compatible converter.',
        'For this converter, respect the listed 110–120V output, total load below 200W and excluded appliance uses. Keep the required leads together with your notes. If a device remains unclear or unsuitable, resolve the question before packing it rather than building the trip around an uncertain connection.'
      ],
      focus: ['Carry the essentials', 'Resolve unclear labels', 'Pack the matching leads']
    },
    D: {
      title: 'Connect what the trip needs',
      deck: 'A useful splitter arrangement can include deliberately empty sockets.',
      intro: [
        'Start with the equipment that earns a place on this journey. Check what will run together and give the most useful devices a clear connection within the vehicle and splitter limits.',
        'Leave an output unused when another device would make the requirements or cable route uncertain. For USB-C, check the maximum that applies to your actual input supply. Set up while parked, keep the console understandable, and make any later adjustment only when safely stopped.'
      ],
      focus: ['Prioritize trip equipment', 'Respect the shared allowance', 'Keep the console clear']
    },
    E: {
      title: 'Make the charger easy to share',
      deck: 'Choose a supported pair for each passenger and keep it recognizable.',
      intro: [
        'Assign devices to the appropriate outputs before departure. Pair each with a suitable cable and keep the arrangement consistent, so a passenger can identify their connection without trial and error.',
        'Use the individual port rating as part of the decision, with a separate requirements check for any laptop. The 125W total does not promise that output to one device. Keep unused leads collected and leave charging questions for a safe stop, away from the driver’s attention.'
      ],
      focus: ['Choose the right output', 'Keep the pairing familiar', 'Collect unused cables']
    },
    F: {
      title: 'Keep the desk easy to disconnect',
      deck: 'Organize around access, clear routes and the equipment you use every day.',
      intro: [
        'Trace each device lead, give it a name and remove the unused cables from the arrangement. Place bulky adapters where the plugs can seat fully, with the switch easy to reach.',
        'Check the actual strip label and circuit limits before reconnecting, and keep the strip directly connected to a suitable grounded wall outlet. Leave the cable route visible enough to inspect. When a new device arrives, revisit the plan rather than treating the next empty outlet as the whole answer.'
      ],
      focus: ['Know every connected device', 'Keep access clear', 'Review after equipment changes']
    }
  }
};
